/**
 * VideoModalKB Component
 * Knowledge Base specific video modal
 * Extracted from KnowledgeBasePage.tsx to reduce file size
 */

import React from 'react';
import type { Video } from '../../types/video';

// Security: Validate YouTube ID format to prevent XSS
const isValidYouTubeId = (id: string): boolean => {
  // YouTube IDs are 11 characters long and contain only alphanumeric characters, hyphens, and underscores
  return /^[a-zA-Z0-9_-]{11}$/.test(id);
};

interface VideoModalKBProps {
  video: Video;
  onClose: () => void;
}

export const VideoModalKB: React.FC<VideoModalKBProps> = ({ video, onClose }) => {
  // Security: Validate YouTube ID before rendering
  if (!video.youtube_id || !isValidYouTubeId(video.youtube_id)) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="bg-white rounded-xl max-w-md w-full p-6 text-center">
          <h3 className="text-xl font-semibold mb-4 text-red-600">Invalid Video</h3>
          <p className="text-gray-600 mb-4">This video cannot be displayed due to security restrictions.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold" style={{ color: '#4A6651' }}>
            {video.title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2"
            style={{ minHeight: '44px', minWidth: '44px' }}
            aria-label="Close video"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div className="relative pb-[56.25%]">
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${encodeURIComponent(video.youtube_id)}?autoplay=1`}
            title={video.title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-presentation"
          />
        </div>
        {video.description && (
          <div className="p-4">
            <p style={{ color: '#598D76' }}>{video.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};